import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Sparkles, User2, LogOut, ShoppingCart } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import Logo from './Logo';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/residential', label: 'Residential' },
  { to: '/commercial', label: 'Commercial' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/shop', label: 'Shop' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/financing', label: 'Financing' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const CartButton = ({ className = '' }) => (
    <button
      onClick={() => { setOpen(false); nav('/cart'); }}
      data-testid="header-cart-button"
      aria-label={`Cart with ${count} items`}
      className={`relative inline-flex items-center justify-center h-10 w-10 rounded-xl border border-[#E6E9EE] text-[#17352D] hover:bg-[#F2F7F5] transition-colors-only ${className}`}
    >
      <ShoppingCart className="h-5 w-5" />
      {count > 0 && (
        <span
          data-testid="header-cart-badge"
          className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8922F] text-white text-[10px] font-bold flex items-center justify-center"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-[#E6E9EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img src="/logo.png" alt="Royal Palm Service" className="h-12 md:h-16 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={`header-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-lg transition-colors-only ${isActive ? 'text-[#17352D] font-semibold' : 'text-slate-600 hover:text-[#17352D] hover:bg-[#F2F7F5]'}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <CartButton />
          <a
            href="tel:5618848900"
            data-testid="header-call-now-button"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#E6E9EE] text-[#17352D] hover:bg-[#F2F7F5] transition-colors-only"
          >
            <Phone className="h-4 w-4" /> <span className="text-sm font-medium whitespace-nowrap">561-884-8900</span>
          </a>
          <Button
            data-testid="header-get-free-estimate-button"
            onClick={() => nav('/estimate')}
            className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Get Free Estimate
          </Button>
          {user ? (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                data-testid="header-portal-button"
                onClick={() => nav(user.role === 'admin' ? '/admin' : user.role === 'commercial' ? '/commercial-portal' : '/portal')}
                className="rounded-xl"
              >
                <User2 className="h-4 w-4 mr-1" /> {user.role === 'admin' ? 'Admin' : 'My Portal'}
              </Button>
              <Button variant="ghost" size="icon" data-testid="header-logout-button" onClick={() => { logout(); nav('/'); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button variant="ghost" data-testid="header-login-button" onClick={() => nav('/login')} className="rounded-xl">
              Sign in
            </Button>
          )}
        </div>

        <div className="flex md:hidden items-center gap-2">
          <CartButton />
          <a
            href="tel:5618848900"
            data-testid="header-mobile-call-button"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#17352D] text-white"
          >
            <Phone className="h-4 w-4" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="header-mobile-menu-button"
                variant="outline"
                size="icon"
                className="rounded-xl"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto">
              <SheetHeader className="text-left">
                <SheetTitle className="sr-only">Royal Palm Services menu</SheetTitle>
                <img src="/logo.png" alt="Royal Palm Service" className="h-14 w-auto object-contain" />
              </SheetHeader>
              <div className="mt-4 flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.to === '/'} onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-lg ${isActive ? 'bg-[#F2F7F5] text-[#17352D] font-semibold' : 'text-slate-700 hover:bg-[#F2F7F5]'}`
                    }>
                    {n.label}
                  </NavLink>
                ))}
                <div className="mt-3 border-t border-[#E6E9EE] pt-3 flex flex-col gap-2">
                  <Button data-testid="mobile-get-estimate-button" onClick={() => { setOpen(false); nav('/estimate'); }} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">
                    <Sparkles className="h-4 w-4 mr-2" /> Get Free Estimate
                  </Button>
                  {user ? (
                    <>
                      <Button variant="outline" onClick={() => { setOpen(false); nav(user.role === 'admin' ? '/admin' : user.role === 'commercial' ? '/commercial-portal' : '/portal'); }} className="rounded-xl">
                        {user.role === 'admin' ? 'Admin Dashboard' : 'My Portal'}
                      </Button>
                      <Button variant="ghost" onClick={() => { setOpen(false); logout(); nav('/'); }} className="rounded-xl">Sign out</Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => { setOpen(false); nav('/login'); }} className="rounded-xl">Sign in</Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
